import { Info, MessageCircleHeart } from "lucide-react";
import { VideoAdvisoryMock } from "@/components/mockups/Mockups";
import { FaqSection } from "@/components/sections/FaqSection";
import { FinalCta } from "@/components/sections/FinalCta";
import { TestimonialsSection } from "@/components/sections/TestimonialsSection";
import { AuditButton, FreeAuditButton } from "@/components/ui/Cta";
import { PageHero } from "@/components/ui/PageHero";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SEO_INSTAGRAM_ADVISORY_EN } from "@/config/seo.en";
import { SEO_INSTAGRAM_ADVISORY_ES } from "@/config/seo.es";
import { useContent } from "@/data/content";
import { getMockups } from "@/data/mockups";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLocale } from "@/i18n/useLocale";

export function InstagramAdvisoryPage() {
  usePageSeo("/asesoria-instagram");
  const locale = useLocale();
  const page = useContent().instagramAdvisoryPage;
  const crumb = (locale === "en" ? SEO_INSTAGRAM_ADVISORY_EN : SEO_INSTAGRAM_ADVISORY_ES).breadcrumb;

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb={crumb}
        title={
          <>
            {page.hero.title} <span className="text-teal-strong">{page.hero.titleAccent}</span>
          </>
        }
        lead={
          <div className="space-y-4">
            <p>{page.hero.subtitle}</p>
            {page.hero.paragraphs.map((paragraph) => (
              <p key={paragraph} className="text-lg text-ink-soft">
                {paragraph}
              </p>
            ))}
          </div>
        }
        actions={
          <>
            <AuditButton location="ig_advisory_hero" variant="primary" />
            <FreeAuditButton location="ig_advisory_hero" variant="outline" />
          </>
        }
        aside={<VideoAdvisoryMock {...getMockups(locale).video} />}
      />

      <section aria-labelledby="advisory-includes" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeader id="advisory-includes" title={page.includes.title} />
          <ul className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {page.includes.groups.map((group, i) => (
              <li
                key={group.title}
                data-reveal=""
                style={{ ["--reveal-delay" as string]: `${(i % 3) * 80}ms` }}
                className="rounded-[1.75rem] border border-brand-primary/10 bg-brand-bg p-7"
              >
                <h3 className="font-display text-xl font-semibold text-brand-primary">{group.title}</h3>
                <ul className="mt-4 space-y-2">
                  {group.items.map((item) => (
                    <li key={item} className="node leading-relaxed text-ink-soft">
                      {item}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section aria-labelledby="advisory-kit" className="on-navy light-pool-navy bg-brand-primary text-white [--pool-x:80%] [--pool-y:10%]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeader
            id="advisory-kit"
            tone="light"
            title={page.kit.kitTitle}
            lead={page.kit.title}
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {page.kit.items.map((item, i) => (
              <article
                key={item.title}
                data-reveal=""
                style={{ ["--reveal-delay" as string]: `${i * 90}ms` }}
                className="rounded-[1.75rem] border border-white/12 bg-white/5 p-8"
              >
                <h3 className="font-display text-2xl font-semibold">{item.title}</h3>
                <p className="mt-3 leading-relaxed text-on-navy-muted">{item.desc}</p>
                {item.details ? (
                  <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                    {item.details.map((detail) => (
                      <li key={detail} className="node text-white">
                        {detail}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
          <p data-reveal="" className="mt-10 max-w-3xl font-display text-xl text-teal-light">
            {page.kit.summary}
          </p>
        </div>
      </section>

      <section aria-label={page.kit.noteTitle} className="bg-brand-bg">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-20 sm:px-6 md:py-24 lg:grid-cols-2 lg:px-8">
          <div data-reveal="" className="rounded-[1.75rem] border border-brand-primary/10 bg-white p-8">
            <h2 className="flex items-center gap-3 font-display text-2xl font-semibold text-brand-primary">
              <Info aria-hidden="true" className="size-6 text-teal-strong" />
              {page.kit.noteTitle}
            </h2>
            <p className="mt-4 leading-relaxed text-ink-soft">{page.kit.note}</p>
          </div>
          <div data-reveal="" style={{ ["--reveal-delay" as string]: "90ms" }} className="rounded-[1.75rem] bg-teal-soft p-8">
            <h2 className="flex items-center gap-3 font-display text-2xl font-semibold text-brand-primary">
              <MessageCircleHeart aria-hidden="true" className="size-6 text-teal-strong" />
              {page.support.title}
            </h2>
            <p className="mt-4 leading-relaxed text-ink-soft">{page.support.description}</p>
            <p className="mt-4 font-semibold text-brand-primary">{page.support.highlight}</p>
          </div>
        </div>
      </section>

      <TestimonialsSection service="asesoria-instagram" />
      <FaqSection path="/asesoria-instagram" />
      <FinalCta
        location="ig_advisory_final"
        lead="advisory"
        title={page.cta.title}
        body={page.cta.description}
      />
    </main>
  );
}
